import React, { createContext, useContext, useState, useMemo } from 'react';
import { useQuery } from 'react-query';
import { useAuth } from './AuthContext';
import { FORMATION_DASHBOARD_API } from '../config/featureFlags';
import { dashboardAPI, spaEntityAPI, formationAPI } from '../services/api';

const AppDataContext = createContext();

export const useAppData = () => {
  const context = useContext(AppDataContext);
  if (!context) {
    throw new Error('useAppData must be used within an AppDataProvider');
  }
  return context;
};

export const AppDataProvider = ({ children }) => {
  const { user } = useAuth();
  
  // Global states
  const [selectedMember, setSelectedMember] = useState(null);
  const [selectedTrainer, setSelectedTrainer] = useState(null);
  const [selectedEquipment, setSelectedEquipment] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);
  
  // Dashboard stats
  const [globalStats, setGlobalStats] = useState({
    totalMembers: 0,
    activeMembers: 0,
    totalRevenue: 0,
    equipmentStatus: "0/0",
    todayAttendance: 0,
    lowStockItems: 0
  });

  // Fetch dashboard overview data from localStorage
  const { data: dashboardData, isLoading: dashboardOverviewLoading } = useQuery(
    'dashboardOverview',
    () => dashboardAPI.getFormationSnapshot(),
    {
      onSuccess: (snap) => {
        const d = snap?.dashboardData?.data || {};
        setGlobalStats({
          totalMembers: d.members?.active || 0,
          activeMembers: d.members?.active || 0,
          totalRevenue: d.revenue?.thisMonth || 0,
          equipmentStatus: '0/0',
          todayAttendance: 0,
          lowStockItems: 0,
        });
      },
      refetchOnWindowFocus: false,
      staleTime: 60 * 1000,
    }
  );

  const { data: formationDashboardApi } = useQuery(
    'formationDashboardStats',
    () => dashboardAPI.getFormationStats(),
    {
      enabled: FORMATION_DASHBOARD_API && Boolean(user),
      staleTime: 60 * 1000,
      retry: 1,
      refetchOnWindowFocus: false,
    }
  );

  /** Gym-era globalStats plus optional Formation API KPI overlay (never overwrites LS-only keys until consumers exist). */
  const globalStatsWithFormation = useMemo(() => {
    const f = FORMATION_DASHBOARD_API ? formationDashboardApi : null;
    if (!f) return globalStats;
    return {
      ...globalStats,
      formationStudentsActive: f.students?.active ?? null,
      formationStudentsTotal: f.students?.total ?? null,
      formationRevenueThisMonth: f.revenue?.thisMonth ?? null,
    };
  }, [globalStats, formationDashboardApi]);

  const { data: notificationsData } = useQuery(
    'notifications',
    async () => {
      const notifications = await spaEntityAPI.list('notifications');
      const unreadCount = notifications.filter((n) => !n.read).length;
        return { notifications, unreadCount };
    },
    {
      onSuccess: (response) => {
        setUnreadCount(response?.unreadCount ?? 0);
      },
      refetchOnWindowFocus: false,
      staleTime: 60 * 1000,
    }
  );

  const emptyList = async () => ({ data: { members: [], trainers: [], equipment: [], subscriptions: [], plans: [] } });

  const membersQuery = useQuery('members', emptyList, { staleTime: 60 * 1000 });
  const subscriptionsQuery = useQuery('subscriptions', emptyList, { staleTime: 60 * 1000 });

  const trainersQuery = useQuery(
    'trainers',
    async () => ({ data: { trainers: await spaEntityAPI.list('trainers') } }),
    { staleTime: 60 * 1000 }
  );

  const equipmentQuery = useQuery('equipment', emptyList, { staleTime: 60 * 1000 });

  const todaySessionsQuery = useQuery(
    'todaySessions',
    async () => {
      const sessions = await spaEntityAPI.list('training-sessions');
        const today = new Date().toISOString().split('T')[0];
      const todaySessions = sessions.filter((session) => {
        const sessionDate = session.date || session.startDate || session.startDateTime;
        return sessionDate && String(sessionDate).split('T')[0] === today;
        });
        return { data: { todaySessions, totalAttendance: todaySessions.length } };
    },
    { staleTime: 60 * 1000 }
  );

  // Add notification (stub - notifications handled by backend)
  const addNotification = (notification) => {
    // Just return an ID for compatibility
    return Date.now();
  };

  // Mark notification as read (stub - notifications handled by backend)
  const markAsRead = (notificationId) => {
    // Function stub
  };

  // Get equipment needing maintenance
  const getMaintenanceNeeded = () => {
    const equipment = equipmentQuery.data?.data?.equipment || [];
    return equipment.filter(item => 
      item.status === 'maintenance' || 
      (item.nextMaintenanceDate && new Date(item.nextMaintenanceDate) <= new Date())
    );
  };

  // Update global stats
  const updateGlobalStats = (newStats) => {
    setGlobalStats(prev => ({
      ...prev,
      ...newStats
    }));
  };

  // Expose the context
  const value = {
    // Data states
    selectedMember,
    setSelectedMember,
    selectedTrainer,
    setSelectedTrainer,
    selectedEquipment,
    setSelectedEquipment,
    notifications: notificationsData?.notifications || [],
    unreadCount,
    globalStats: globalStatsWithFormation,
    
    // Data access functions
    getMembers: () => membersQuery.data?.data?.members || [],
    getMemberById: (id) => {
      const members = membersQuery.data?.data?.members || [];
      return members.find(member => member.id === id);
    },
    
    getTrainers: () => trainersQuery.data?.data?.trainers || [],
    getTrainerById: (id) => {
      const trainers = trainersQuery.data?.data?.trainers || [];
      return trainers.find(trainer => trainer.id === id);
    },
    
    getEquipment: () => equipmentQuery.data?.data?.equipment || [],
    getEquipmentById: (id) => {
      const equipment = equipmentQuery.data?.data?.equipment || [];
      return equipment.find(item => item.id === id);
    },
    
    getSubscriptionPlans: () => subscriptionsQuery.data?.data?.plans || [],
    getSubscriptionPlanById: (id) => {
      const plans = subscriptionsQuery.data?.data?.plans || [];
      return plans.find(plan => plan.id === id);
    },
    
    getSubscriptions: () => subscriptionsQuery.data?.data?.subscriptions || [],
    getSubscriptionById: (id) => {
      const subscriptions = subscriptionsQuery.data?.data?.subscriptions || [];
      return subscriptions.find(sub => sub.id === id);
    },
    
    getActiveSubscriptions: () => {
      const subscriptions = subscriptionsQuery.data?.data?.subscriptions || [];
      return subscriptions.filter(sub => sub.isActive);
    },
    
    getExpiringSubscriptions: () => {
      const subscriptions = subscriptionsQuery.data?.data?.subscriptions || [];
      const nextWeek = new Date();
      nextWeek.setDate(nextWeek.getDate() + 7);
      
      return subscriptions.filter(sub => {
        const endDate = new Date(sub.endDate);
        return sub.isActive && endDate <= nextWeek && endDate >= new Date();
      });
    },
    
    calculateTotalRevenue: () => {
      const subscriptions = subscriptionsQuery.data?.data?.subscriptions || [];
      return subscriptions
        .filter(sub => sub.isActive)
        .reduce((total, sub) => total + (sub.price || 0), 0);
    },
    
    // Utility functions
    addNotification,
    markAsRead,
    getMaintenanceNeeded,
    updateGlobalStats,
    
    // Loading states
    isLoading: {
      members: membersQuery.isLoading,
      subscriptions: subscriptionsQuery.isLoading,
      trainers: trainersQuery.isLoading,
      equipment: equipmentQuery.isLoading,
      todaySessions: todaySessionsQuery.isLoading,
      dashboard: dashboardOverviewLoading,
    }
  };

  return <AppDataContext.Provider value={value}>{children}</AppDataContext.Provider>;
};

export default AppDataContext; 